import React, { useEffect, useState } from 'react';
import { Column, Columns } from '../../../styled/Columns/wrappers';
import Container from '../../../styled/Container';
import Distance from '../../../styled/Distance';
import {
  FlagContainer,
  Left,
  MarkingsWrapper,
  ProductListingWrapper,
  ProductPageGrid,
  Right,
  TestFreaksBadge,
} from '../../../styled/PageGrid/product';
import MainBadge from './badges/MainBadge';
import Breadcrumbs from './Breadcrumbs';
import EnergyBadge from './badges/EnergyBadge';
import Header from './Header';
import PriceBadge from './badges/PriceBadge';
import ProductProvider from './providers/ProductProvider';
import Text from '../../../styled/Text';
import ProductMedia from './ProductMedia';
import VariantSelection from './VariantSelection';
import BuyButton from './BuyButton';
import PartPayment from './PartPayment';
import Description from './Description';
import CheckList from '../../../styled/CheckList';
import MobileBuyButton from './BuyButton/MobileBuyButton';
import ProductInfo from './ProductInfo';
import EnvironmentImages from './EnvironmentImages';
import Configuration from './Configuration';
import AddToCartConfirmation from './AddToCartConfirmation';
import StockStatus from './StockStatus';
import ViewportLoader from '../../../components-legacy/ViewportLoader';
import Skeleton from '../../../components-legacy/Skeleton';
import ProductStructuredData from './ProductStructuredData';
import CartErrorModal from '../../../components-legacy/CartErrorModal';
import Gs1StructuredData from './Gs1StructuredData';
import { AssemblyInfo } from './AssemblyInfo';
import AttributeFlag from './badges/AttributeBadge';
import Price from './Price';
import NewsTitle from './badges/NewsTitle';
import ALPInformationBox from './badges/ALPInformationBox';
import VariantsInStock from './VariantsInStock';
import OutletInformationBox from './badges/OutletInformationBox';
import { BadgeButton, loadBadge, ReviewProvider } from '../../../components-legacy/Review';
import { EFIInformation } from '../../../styled/EfiText/index';

const Accessories = React.lazy(() => import('./Similar/Accessories'));
const Siblings = React.lazy(() => import('./Similar/Siblings'));
const SameSerie = React.lazy(() => import('./SameSerie'));
const ProductSeries = React.lazy(() => import('./ProductSeries'));
const SameCategory = React.lazy(() => import('./SameCategory'));

interface IProductPage {
  configuration?: Partial<Record<ConfigurationType, string>>;
  configurationId?: string;
  product: IArticle;
  configurationMetaData: Record<string, IConfigurationMetaData>;
  closestVariantUrl?: string;
}

const ProductPage = (props: IProductPage) => {
  const [interactWithReviewBadge, setInteractWithReviewBadge] = useState(false);
  const [hasReviews, setHasReviews] = useState(false);
  const handleReviews = (shouldShowReviews: boolean) => {
    setHasReviews(shouldShowReviews);
  };
  const handleClick = () => {
    setInteractWithReviewBadge((prev) => !prev);
  };

  useEffect(() => {
    loadBadge(props.product);
  }, [props.product.groupId]);

  return (
    <div>
      <Distance size={['2', '4']} />
      <ProductProvider {...props}>
        <Container>
          <ProductPageGrid>
            <Left>
              <Breadcrumbs />
              <Distance size="2" />
            </Left>

            <Right>
              <NewsTitle />

              <Header />

              <div className="hidden-lt-md">
                <Distance size="1" />

                <PriceBadge />

                <Columns className="space-2 spacebtw-center">
                  <Column>
                    <Price />
                  </Column>

                  <Column>
                    <EnergyBadge />
                  </Column>
                </Columns>
                <Distance size="2" />
              </div>

              <MainBadge />

              <Distance size="2" />
              <TestFreaksBadge>
                <div className="testfreaks-section">
                  <BadgeButton onClick={handleClick} handleReviews={handleReviews} />
                </div>
              </TestFreaksBadge>
            </Right>

            <Left>
              <MarkingsWrapper>
                <Text align="right" className="hidden-md">
                  <EnergyBadge />
                </Text>

                <FlagContainer className="product-page">
                  <AttributeFlag />
                </FlagContainer>
              </MarkingsWrapper>
              <ProductMedia />
              <Distance size="5" />
              <div className="hidden-lt-md">
                <EFIInformation items={props.product.description.efiText} />
              </div>

              <Distance size="4" />
            </Left>

            <Right>
              <Configuration distance="3" />

              <VariantSelection distance="3" />

              <div className="hidden-md">
                <EFIInformation items={props.product.description.efiText} />
                <Distance size="3" />
              </div>

              <BuyButton />

              <ALPInformationBox />

              <OutletInformationBox />

              <PartPayment />

              <StockStatus />

              <AssemblyInfo />

              <VariantsInStock />

              <Distance line size={['3', '4']} />

              <Description distance="3" />

              <CheckList items={props.product.usps} />

              <Distance size="4" className="hidden-lt-md" />

              <Distance size="2" />
            </Right>

            <Left>
              <ReviewProvider interactWithReviewBadge={interactWithReviewBadge}>
                <ProductInfo hasReviews={hasReviews} />
              </ReviewProvider>

              <Distance size="4" className="hidden-lt-md" />

              <EnvironmentImages className="hidden-lt-md" />

              <Distance size="4" className="hidden-lt-md" />
            </Left>

            <Right>
              <MobileBuyButton />
            </Right>
          </ProductPageGrid>

          <Distance size={['4', '8']} />
          <ViewportLoader loader={<Skeleton height="385px" />}>
            <Accessories eventContext="product" />
          </ViewportLoader>

          <ViewportLoader loader={<Skeleton height="385px" />}>
            <Siblings />
          </ViewportLoader>
        </Container>

        <ProductListingWrapper>
          <Container yPadding="p-y-md">
            <ViewportLoader loader={<Skeleton height="385px" />}>
              <SameSerie />
            </ViewportLoader>
          </Container>
        </ProductListingWrapper>

        <Container>
          <ViewportLoader loader={<Skeleton height="385px" />}>
            <ProductSeries />
          </ViewportLoader>
        </Container>

        <ProductListingWrapper>
          <Container yPadding="p-y-md">
            <ViewportLoader loader={<Skeleton height="385px" />}>
              <SameCategory />
            </ViewportLoader>
          </Container>
        </ProductListingWrapper>

        <AddToCartConfirmation />

        <ProductStructuredData />

        <Gs1StructuredData />
      </ProductProvider>
      <CartErrorModal />
    </div>
  );
};

export default ProductPage;
